import { useCallback, useEffect, useRef } from 'react'
import * as React from 'react'
import MerchandiseCarouselSection from '../MerchandiseCarouselSection'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import useMerchandiseList from 'hooks/Merchandise/useMerchandiseList'
import { fillArray, take } from 'lib/array/arrayUtils'
import MerchandiseOfferCard from '../MerchandiseCards/MerchandiseOfferCard'
import { rem } from 'polished'
import MerchandiseCardSkeleton from '../MerchandiseCards/MerchandiseCardSkeleton'
import { useInView } from 'react-intersection-observer'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'

interface Props {
  title: React.ReactNode
  trackingListName: string
  trackingPageName: string
  subtitle?: React.ReactNode
  viewAllTo?: string
  /**
   * Supply filters to have the carousel manage
   * the list of merchandise that will be passed in
   */
  filters?: App.MerchandiseFilters
  /**
   * Provide your own list of merchandise IDS
   */
  overrideList?: Array<App.MerchandiseSearchAdvert>
  /**
   * Provide your own state for whether the list is fetching or not
   */
  overrideFetching?: boolean
}

function MerchandiseOfferCarousel(props: Props) {
  const { title, trackingListName, trackingPageName, filters, subtitle, viewAllTo, overrideList, overrideFetching } = props

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  const [list, fetchingList] = useMerchandiseList(filters, { disabled: !!overrideList || !carouselInView })

  const finalList = overrideList ?? list

  const carouselRef = useRef<HTMLDivElement | null>(null)

  const fetching = !carouselInView || (overrideFetching ?? fetchingList)

  useEffect(() => {
    // We've been given a new offer list, send the carousel back to the start
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0 })
    }
  }, [filters])

  const onCarouselRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      inViewRef(node)
      carouselRef.current = node
    }
  }, [inViewRef])

  const trackingConfig = useOfferListTracking(
    getListName('OfferList', `Merchandise ${trackingPageName}`, trackingListName), { key: JSON.stringify(filters) },
  )

  if (!fetching && finalList.length < 4) {
    return null
  }

  return <LoaderPlayStateContext value={carouselInView ? 'running' : 'paused'}>
    <MerchandiseCarouselSection
      title={title}
      subtitle={subtitle}
      viewAllTo={viewAllTo}
      trackingName={trackingListName}
      ref={onCarouselRef}
    >
      <OfferListEventsProvider tracking={trackingConfig}>
        <FullWidthCarousel
          width={rem(240)}
          tabletWidth={rem(270)}
          snap="start"
          itemsPerArrow={3}
        >
          {fetching && fillArray(8).map(i => <MerchandiseCardSkeleton key={i} />)}
          {!fetching && take(finalList, 20).map((searchAdvert, index) => <MerchandiseOfferCard
            key={searchAdvert.id}
            id={searchAdvert.id}
            index={index}
          />)}
        </FullWidthCarousel>
      </OfferListEventsProvider>
    </MerchandiseCarouselSection>
  </LoaderPlayStateContext>
}

export default MerchandiseOfferCarousel
